import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { from, switchMap } from 'rxjs';

import { AccessKeyProcessor } from '../../core/access-key-processor.service';
import { ApiEndpoints } from '../../core/api-endpoints.service';
import { CookieService } from '../../core/cookie.service';
import { IdService } from '../../core/id.service';
import { JoseService } from '../../core/jose.service';
import { StorageService } from '../../core/storage.service';
import {
  AccessRequest,
  DataRecord,
  DEFAULT_LOCKED_SPACE,
  WorkflowState,
} from '../../core/workflow-state.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';
import { epochSecondsFromNow } from '../../shared/date-utils';

interface SdkIdentity {
  id: string;
  key: string;
  value: string;
}

@Component({
  selector: 'app-access-key',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './access-key.component.html',
})
export class AccessKeyComponent {
  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly ids = inject(IdService);
  private readonly cookies = inject(CookieService);
  private readonly jose = inject(JoseService);
  private readonly processor = inject(AccessKeyProcessor);
  private readonly storage = inject(StorageService);
  private readonly workflow = inject(WorkflowState);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly endpoint = this.endpoints.url(this.endpoints.accessKey);
  protected readonly call = new ApiCallState();
  protected readonly requestId = signal(this.ids.shortId());
  protected readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  protected readonly sdkIdentities = signal<SdkIdentity[]>([]);
  protected readonly requestOptions = computed(() =>
    Array.from(this.workflow.accessRequests(), ([accessId, value]) => ({
      accessId,
      itemId: String(value['itemId'] ?? ''),
      value,
    })),
  );
  protected readonly selectedRequests = computed(() =>
    this.requestOptions().filter((item) => this.selectedIds().has(item.accessId)),
  );
  protected readonly accessKeys = computed(() => Array.from(this.workflow.accessKeys().values()));

  protected language = 'en-US';
  protected keyUserId = String(DEFAULT_LOCKED_SPACE['keyUserId'] ?? 'PNDelPrsnl1234');
  protected requesterId = String(DEFAULT_LOCKED_SPACE['requesterId'] ?? 'ID1234');
  protected shipperName = String(DEFAULT_LOCKED_SPACE['shipperName'] ?? 'Postnord');
  protected readonly publicKey = this.jose.publicKey;
  protected readonly algorithm = this.jose.algorithm;
  protected readonly encryption = this.jose.encryption;

  protected toggleRequest(accessId: string, selected: boolean): void {
    const next = new Set(this.selectedIds());
    if (selected) {
      next.add(accessId);
    } else {
      next.delete(accessId);
    }
    this.selectedIds.set(next);
  }

  protected regenerateRequestId(): void {
    this.requestId.set(this.ids.shortId());
  }

  protected addSdkIdentity(): void {
    this.sdkIdentities.update((items) => [
      ...items,
      { id: this.ids.uuid(), key: '', value: '' },
    ]);
  }

  protected removeSdkIdentity(id: string): void {
    this.sdkIdentities.update((items) => items.filter((item) => item.id !== id));
  }

  protected submit(): void {
    const deliveryItems: string[] = [];
    const itemMap = new Map<string, string>();

    for (const selected of this.selectedRequests()) {
      const itemId = selected.itemId;
      const mapId = String(selected.value['mapId'] ?? itemId);
      if (!itemId) {
        continue;
      }
      deliveryItems.push(itemId);
      itemMap.set(itemId, mapId);
      this.cookies.write(itemId, mapId, 60);
    }

    const payload = {
      requestor: {
        shipperName: this.shipperName,
        requesterId: this.requesterId,
      },
      ttl: epochSecondsFromNow(5),
      language: this.language,
      keyUserId: this.keyUserId,
      requestId: this.requestId(),
      deliveryItems,
      encryption: {
        key: this.publicKey,
        alg: this.algorithm,
        enc: this.encryption,
        lockProviderSdkIdentities: this.sdkIdentities()
          .filter(({ key, value }) => key && value)
          .map(({ key, value }) => ({ [key]: value })),
      },
    };

    const cache = this.storage.get<Record<string, string>>('accessKeyCache', {});
    for (const itemId of deliveryItems) {
      cache[itemId] = this.keyUserId;
    }
    this.storage.set('accessKeyCache', cache);
    this.call.start(payload);

    this.http
      .post<DataRecord>(this.endpoint, payload)
      .pipe(
        switchMap((response) => from(this.processor.process(response, itemMap, cache))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.cookies.write('keyRequestId', this.requestId(), 60);
          this.call.succeed(response);
        },
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected requestLabel(request: AccessRequest): string {
    return String(request['itemId'] ?? request.accessId ?? 'Unknown item');
  }
}